import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Compass, Loader2, MapPin, ChevronRight, HelpCircle, Bell, BellRing,
  MessagesSquare, Camera, CalendarDays, Lightbulb, Newspaper, BedDouble, UtensilsCrossed } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api from "../lib/api";
import { setNoindex } from "../lib/seo";
import { useAuth } from "../context/AuthContext";
import ListenButton from "../components/ListenButton";
import GeoContributions from "../components/GeoContributions";
import NewsFeed from "../components/NewsFeed";
import NearbyPlaces from "../components/NearbyPlaces";
import Weather from "../components/Weather";
import PhotoGallery from "../components/PhotoGallery";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";
import UnsplashGallery, { placePhotoQuery } from "../components/UnsplashGallery";
import GuidePhotoMap from "../components/GuidePhotoMap";
import GeoEvents from "../components/GeoEvents";
import HotelProviderSearch from "../components/travel/HotelProviderSearch";

/* Geographic Content Engine — destination guide page.
   PUBLISHED -> full guide with breadcrumb + section quick-nav.
   Missing -> "Collecting details about {name}..." card that polls while a
   background job generates the article (visitor never has to wait or stay). */

export const geoSlug = (s) => String(s || "").toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const GOOGLE_COLORS = [
  { bg: "#34A853", soft: "text-green-50" }, { bg: "#4285F4", soft: "text-blue-50" },
  { bg: "#EA4335", soft: "text-red-50" },
];
// deterministic "random" per place — char-code hash (Kochi -> green)
const themeFor = (slug) => {
  let h = 0;
  for (const ch of slug || "") h += ch.charCodeAt(0);
  return GOOGLE_COLORS[h % GOOGLE_COLORS.length];
};

/* Hand-written special features linked from specific guides (next to the Listen control). */
const SPECIAL_ARTICLES = {
  "india/kerala/kochi": [{ to: "/articles/special/our-cochin", label: "Our Cochin: The Kochi and Ernakulam We Remember" }],
  "india/kerala/cochin": [{ to: "/articles/special/our-cochin", label: "Our Cochin: The Kochi and Ernakulam We Remember" }],
  "india/kerala/ernakulam": [{ to: "/articles/special/our-cochin", label: "Our Cochin: The Kochi and Ernakulam We Remember" }],
  "india/kerala": [
    { to: "/articles/special/kerala-ayurveda", label: "Ayurveda in Kerala: Healing, Wellness & Medical Travel" },
    { to: "/articles/special/the-chenda", label: "The Chenda Goes Global: Kerala's Thunderous Drum" },
  ],
};

function Collecting({ name }) {
  return (
    <div className="max-w-2xl mx-auto my-16 border border-zinc-300 bg-white p-8 text-center" data-testid="geo-collecting">
      <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#4285F4]" />
      <h2 className="font-heading font-900 text-2xl mt-4">Collecting details about {name}…</h2>
      <p className="text-sm text-zinc-600 mt-3 max-w-md mx-auto">
        We're gathering useful travel, transportation, accommodation, food, safety and local
        information for this destination. You can keep exploring TravelerPulse — this guide
        will be ready shortly and will be here when you come back.
      </p>
      <Link to="/destinations" className="inline-block mt-5 px-4 py-2 border border-zinc-300 text-sm font-700 hover:bg-zinc-50">
        Keep exploring
      </Link>
    </div>
  );
}

export default function ExploreGuide() {
  const params = useParams();
  const slug = params["*"] || "";
  const [a, setA] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await api.get(`/geo-content/${slug}`);
        if (stop) return;
        setA(r.data);
        if (r.data.status !== "PUBLISHED") {
          pollRef.current = setTimeout(load, 8000);
        }
      } catch {
        if (!stop) pollRef.current = setTimeout(load, 15000);
      }
    };
    setA(null);
    load();
    return () => { stop = true; clearTimeout(pollRef.current); };
  }, [slug]); // hash-only changes (tab bookmarks) must NOT refetch // eslint-disable-line react-hooks/exhaustive-deps

  // SEO: auto-generated guide shells (generating/failed) carry no original value —
  // keep them out of search indexes until published (Feb-2026 site review).
  useEffect(() => {
    setNoindex(!!a && a.status !== "PUBLISHED");
    return () => setNoindex(false);
  }, [a?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const [nearby, setNearby] = useState(undefined);

  useEffect(() => {
    setNearby(undefined);
    if (!slug || !a || a.status !== "PUBLISHED") return;
    api.get("/geo/nearby", { params: { slug, limit: 12 } })
      .then((r) => setNearby(r.data)).catch(() => setNearby({ count: 0, items: [], center: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, a?.status]);

  // Bookmark support: arriving with a #hash (e.g. /explore/...#forum) scrolls to that section.
  useEffect(() => {
    if (a?.status !== "PUBLISHED" || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start" }), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a?.status]);

  if (!slug) return <Collecting name="this destination" />;
  if (a === null) return (
    <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-zinc-400" /></div>
  );

  const h = a.hierarchy || {};
  const parts = slug.split("/");
  const crumbs = [
    { label: h.country || parts[0], to: `/explore/${parts[0]}` },
    parts[1] && { label: h.state || parts[1], to: `/explore/${parts[0]}/${parts[1]}` },
    parts[2] && { label: h.city || parts[2], to: `/explore/${slug}` },
  ].filter(Boolean);
  const theme = themeFor(slug);

  if (a.status !== "PUBLISHED") return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-4xl mx-auto px-5 pt-6">
        <nav className="text-xs font-mono text-zinc-500 flex items-center gap-1 flex-wrap" data-testid="geo-breadcrumb">
          <Link to="/destinations" className="hover:underline">Explore</Link>
          {crumbs.map((c) => (
            <span key={c.to} className="flex items-center gap-1"><ChevronRight className="w-3 h-3" />
              <Link to={c.to} className="hover:underline">{c.label}</Link></span>
          ))}
        </nav>
      </div>
      <Collecting name={a.name} />
    </div>
  );

  const glance = a.at_a_glance || {};
  const glanceRows = [["Known for", glance.known_for], ["Best time", glance.best_time],
    ["Suggested stay", glance.suggested_stay], ["Language", glance.language],
    ["Currency", glance.currency], ["Time zone", glance.timezone]].filter(([, v]) => v);
  const nearbyBase = a.place_type === "country" ? slug : parts.slice(0, -1).join("/");

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="geo-guide">
      <div style={{ backgroundColor: theme.bg }} className="text-white relative overflow-hidden">
        {a.hero_image && (
          <>
            <img src={a.hero_image} alt={`${a.name} — hero`} data-testid="geo-hero-image"
              className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.65) 100%)" }} />
          </>
        )}
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10 relative">
          <nav className="text-xs font-mono flex items-center gap-1 flex-wrap opacity-90 mb-4" data-testid="geo-breadcrumb">
            <Link to="/destinations" className="hover:underline">Explore</Link>
            {crumbs.map((c) => (
              <span key={c.to} className="flex items-center gap-1"><ChevronRight className="w-3 h-3" />
                <Link to={c.to} className="hover:underline">{c.label}</Link></span>
            ))}
          </nav>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3">
            <Compass className="w-9 h-9 text-[#FBBC05]" /> {a.name} Travel Guide
          </h1>
          <p className={`${theme.soft} mt-2 text-base md:text-lg`}>
            {[h.city && h.state, h.country].filter(Boolean).join(", ") || a.place_type}
          </p>
          {nearby?.center && (
            <div className="mt-3 inline-block bg-white/15 backdrop-blur-sm px-3 py-1.5" data-testid="geo-weather">
              <Weather lat={nearby.center.lat} lon={nearby.center.lon} label={a.name} compact />
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {(a.sections || []).map((s) => (
              <a key={s.id} href={`#${s.id}`}
                className="px-2.5 py-1 bg-white/15 hover:bg-white/25 text-xs font-700 backdrop-blur-sm"
                data-testid={`geo-nav-${s.id}`}>{s.heading}</a>
            ))}
            {(a.faqs || []).length > 0 && <a href="#faq" className="px-2.5 py-1 bg-white/15 hover:bg-white/25 text-xs font-700">FAQ</a>}
          </div>
        </div>
      </div>

      <GuideBookmarkTabs accent={theme.bg}
        hasFood={(a.sections || []).some((s) => s.id === "food_drink")} />

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <ListenButton targetId="geo-article-body" label="Listen to this guide" />
          {SPECIAL_ARTICLES[slug] && (
            <span className="inline-flex flex-col gap-1.5">
              {SPECIAL_ARTICLES[slug].map((sp) => (
                <Link key={sp.to} to={sp.to} data-testid="special-article-link"
                  className="inline-flex items-center gap-1.5 border-2 border-emerald-600 bg-emerald-50 text-emerald-800 px-3 py-2 text-sm font-700 hover:bg-emerald-100 transition-colors">
                  {sp.label} <ChevronRight className="w-4 h-4 shrink-0" />
                </Link>
              ))}
              <Link to="/specials" className="text-[11px] text-emerald-700 hover:underline self-end" data-testid="guide-specials-link">All TravelerPulse Specials →</Link>
            </span>
          )}
          <span className="text-[11px] text-muted-foreground font-mono">
            Guide generated {a.published_at?.slice(0, 10)} · time-sensitive facts (visas, prices, schedules) — verify on official sites
          </span>
        </div>

        {glanceRows.length > 0 && (
          <div className="bg-white border border-zinc-300 p-5 mb-8" data-testid="geo-at-a-glance">
            <div className="font-heading font-900 text-lg mb-3">At a Glance</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {glanceRows.map(([k, v]) => (
                <div key={k} className="text-sm"><span className="font-700 text-zinc-500 text-xs uppercase font-mono">{k}: </span>{v}</div>
              ))}
            </div>
          </div>
        )}

        {CITY_DIRECTORIES[slug] && (
          <Link to={CITY_DIRECTORIES[slug].to} data-testid="country-city-directory-link"
            className="mb-6 flex items-center justify-between gap-3 border border-zinc-300 bg-amber-50 px-4 py-3 hover:border-amber-500 transition-colors">
            <span className="text-sm font-700">{CITY_DIRECTORIES[slug].label}</span>
            <ChevronRight className="w-4 h-4 shrink-0" />
          </Link>
        )}

        <UnsplashGallery query={placePhotoQuery(a.name, h.country)} name={a.name} accent={theme.bg} />

        <div id="geo-article-body">
          {(a.sections || []).map((s) => (
            <section key={s.id} id={s.id} className="mb-8 scroll-mt-36" data-testid={`geo-section-${s.id}`}>
              <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-3" style={{ borderColor: theme.bg }}>{s.heading}</h2>
              <div className="prose prose-sm prose-zinc max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_h3]:font-heading [&_h3]:font-900">
                <ReactMarkdown>{s.markdown}</ReactMarkdown>
              </div>
            </section>
          ))}

          {(a.faqs || []).length > 0 && (
            <section id="faq" className="mb-8 scroll-mt-36" data-testid="geo-faqs">
              <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-3" style={{ borderColor: theme.bg }}>Traveler FAQ</h2>
              {a.faqs.map((f, i) => (
                <details key={i} className="bg-white border border-zinc-200 mb-2 group">
                  <summary className="px-4 py-3 text-sm font-700 cursor-pointer flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 shrink-0" style={{ color: theme.bg }} /> {f.q}
                  </summary>
                  <div className="px-4 pb-3 text-sm text-zinc-700">{f.a}</div>
                </details>
              ))}
            </section>
          )}
        </div>

        {(a.nearby || []).length > 0 && (
          <section className="mb-8 scroll-mt-36" data-testid="geo-nearby">
            <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-3" style={{ borderColor: theme.bg }}>Places Near {a.name}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {a.nearby.map((n) => (
                <Link key={n.name} to={`/explore/${nearbyBase}/${geoSlug(n.name.split("(")[0])}`}
                  className="bg-white border border-zinc-300 p-3 hover:border-zinc-500 transition-colors"
                  data-testid={`geo-nearby-${geoSlug(n.name.split("(")[0])}`}>
                  <div className="font-700 text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" style={{ color: theme.bg }} /> {n.name}</div>
                  {n.why && <div className="text-xs text-zinc-600 mt-1">{n.why}</div>}
                </Link>
              ))}
            </div>
          </section>
        )}

        <GeoNewsTabs name={a.name} region={a.place_type === "city" ? h.state || h.country : h.country}
          country={h.country} slug={slug} accent={theme.bg} />

        <GuidePhotoMap slug={slug} name={a.name} accent={theme.bg} />

        <div id="nearby" className="scroll-mt-36">
          <NearbyPlaces slug={slug} accent={theme.bg} data={nearby || null} />
        </div>

        <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-zinc-200">
          <Link to={`/food-dining/${slug}`} className="px-4 py-2 border border-orange-600 text-orange-700 text-sm font-700 hover:bg-orange-50" data-testid="geo-cta-food">Food &amp; Dining in {a.name}</Link>
          <Link to={`/hotels-accommodation/${slug}`} className="px-4 py-2 border border-teal-700 text-teal-800 text-sm font-700 hover:bg-teal-50" data-testid="geo-cta-stay">Hotels &amp; Accommodation in {a.name}</Link>
          <a href={`/hotels?place=${encodeURIComponent(a.name)}&campaign=TP-DESTINATION`} className="px-4 py-2 border border-teal-600 text-teal-700 text-sm font-700 hover:bg-teal-50" data-testid="geo-cta-hotels">Find hotels in {a.name}</a>
          <Link to="/visas" className="px-4 py-2 border border-zinc-300 text-sm font-700 hover:bg-zinc-50">Check visa requirements</Link>
          <Link to="/travel-hub" className="px-4 py-2 border border-zinc-300 text-sm font-700 hover:bg-zinc-50">Plan a trip in My Travel Hub</Link>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4">
          {(a.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-2 py-0.5 bg-zinc-100 text-zinc-600">{t}</span>)}
        </div>

        <section id="hotels" className="mt-12 scroll-mt-36" data-testid="geo-hotels">
          <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-4" style={{ borderColor: theme.bg }}>
            Hotels in {a.name}
          </h2>
          <HotelProviderSearch initial={{ place: a.name }} campaign="TP-DESTINATION"
            placement="guide-page" heading="Search stays for your dates" />
        </section>

        <section id="events" className="mt-12 scroll-mt-36" data-testid="geo-events-section">
          <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-4" style={{ borderColor: theme.bg }}>
            Events & Festivals in {a.name}
          </h2>
          <GeoEvents slug={slug} placeName={a.name} />
        </section>

        <section id="tips" className="mt-12 scroll-mt-36" data-testid="geo-community">
          <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-1" style={{ borderColor: theme.bg }}>
            Know {a.name}? Help travelers.
          </h2>
          <p className="text-xs text-muted-foreground mb-4">Traveler-shared tips, places, warnings and photos — community content, separate from the editorial guide above.</p>
          <GeoContributions slug={slug} placeName={a.name} />
          <div className="mt-6">
            <AskCommunityCTA entityName={a.name} targetId="forum" />
          </div>
          <div id="photos" className="mt-8 scroll-mt-36">
            <PhotoGallery entityType="geo" entityId={slug} />
          </div>
          <div id="forum" className="mt-8 scroll-mt-36">
            <DiscussionBoard entityType="geo" entityId={slug} entityName={a.name}
              title={`${a.name} traveler forum`} placeholder={`Ask or share something about ${a.name}…`} />
          </div>
        </section>
      </div>
    </div>
  );
}

/* Bookmarked quick-access tabs — sticky under the navbar so long guides never
   bury the community sections. Each tab scrolls to its section and writes a
   #hash bookmark into the URL (shareable / revisitable). */
const GUIDE_TABS = [
  { id: "forum", label: "Traveler Forum", icon: MessagesSquare },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "tips", label: "Travel Experts / Tips", icon: Lightbulb },
  { id: "news", label: "News", icon: Newspaper },
  { id: "nearby", label: "Nearby Places", icon: MapPin },
  { id: "hotels", label: "Hotels", icon: BedDouble },
  { id: "food_drink", label: "Restaurants", icon: UtensilsCrossed },
];

function GuideBookmarkTabs({ accent, hasFood }) {
  const [active, setActive] = useState(null);
  const [top, setTop] = useState(0);
  const tabs = GUIDE_TABS.filter((t) => t.id !== "food_drink" || hasFood);

  useEffect(() => {
    const header = document.querySelector("header");
    const update = () => setTop(header ? header.offsetHeight : 0);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-140px 0px -55% 0px" });
    tabs.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [hasFood]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActive(id);
  };

  return (
    <div style={{ top }} className="sticky z-30 bg-white/95 backdrop-blur border-b border-zinc-200 shadow-sm"
      data-testid="guide-tabs">
      <div className="max-w-4xl mx-auto px-3 sm:px-5 lg:px-8 flex flex-wrap justify-center sm:justify-start sm:flex-nowrap gap-0.5 sm:overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => go(t.id)} data-testid={`guide-tab-${t.id}`}
            style={active === t.id ? { borderColor: accent, color: accent } : undefined}
            className={`inline-flex items-center gap-1.5 px-2 sm:px-3 py-2 sm:py-2.5 text-xs font-700 border-b-2 whitespace-nowrap transition-colors shrink-0 ${
              active === t.id ? "" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* Country guides that have a dedicated regional city directory page. */
const CITY_DIRECTORIES = {
  "united-states": { to: "/us-cities", label: "Browse nearly 400 US city guides by region →" },
  india: { to: "/india-cities", label: "Browse 137 India city guides by region →" },
  "united-kingdom": { to: "/uk-cities", label: "Browse 90+ UK city guides by region →" },
  "united-arab-emirates": { to: "/uae-cities", label: "Browse UAE city guides across all seven emirates →" },
  australia: { to: "/au-cities", label: "Browse 50+ Australia city guides by state →" },
  japan: { to: "/world-cities/japan", label: "Browse 40+ Japan city guides by region →" },
  malaysia: { to: "/world-cities/malaysia", label: "Browse Malaysia city guides by region →" },
  indonesia: { to: "/world-cities/indonesia", label: "Browse Indonesia city guides by region →" },
  "south-korea": { to: "/world-cities/south-korea", label: "Browse South Korea city guides by region →" },
  philippines: { to: "/world-cities/philippines", label: "Browse Philippines city guides by region →" },
  china: { to: "/world-cities/china", label: "Browse 35+ China city guides by region →" },
  russia: { to: "/world-cities/russia", label: "Browse Russia city guides by region →" },
};
// Every European country guide links to the 150-place Europe directory
["france", "italy", "spain", "portugal", "germany", "austria", "switzerland",
  "the-netherlands", "netherlands", "belgium", "luxembourg", "ireland", "greece", "cyprus",
  "denmark", "sweden", "norway", "finland", "iceland", "czechia", "hungary", "poland",
  "slovakia", "slovenia", "croatia", "montenegro", "bosnia-and-herzegovina", "serbia",
  "albania", "north-macedonia", "bulgaria", "romania", "estonia", "latvia", "lithuania",
  "turkey", "malta", "monaco", "san-marino", "andorra", "liechtenstein"].forEach((c) => {
  CITY_DIRECTORIES[c] = { to: "/world-cities/europe", label: "Browse 230+ Europe city guides by region →" };
});

/* City news with category tabs — reuses the Travel News category engine scoped to this place. */
const GEO_NEWS_TABS = [
  ["general", "Local News"],
  ["hotels", "Hotels & Stays"],
  ["impacting", "Disruptions"],
  ["deals", "Deals"],
];

function GeoNewsTabs({ name, region, country, slug, accent }) {
  const [cat, setCat] = useState("general");
  const q = encodeURIComponent([name, region].filter(Boolean).join(" "));
  return (
    <section id="news" className="mb-8 scroll-mt-36" data-testid="geo-news">
      <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-3" style={{ borderColor: accent }}>
        {name} in the News
      </h2>
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {GEO_NEWS_TABS.map(([id, label]) => (
          <button key={id} onClick={() => setCat(id)} data-testid={`geo-news-tab-${id}`}
            className={`px-3 py-1.5 text-xs font-700 border transition-colors ${cat === id
              ? "text-white" : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"}`}
            style={cat === id ? { backgroundColor: accent, borderColor: accent } : {}}>
            {label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-3">
          <DealAlertButton slug={slug} name={name} country={country} />
          <Link to="/travel-news" data-testid="geo-news-see-all"
            className="text-xs text-blue-700 font-600 hover:underline">All travel news →</Link>
        </span>
      </div>
      <NewsFeed path={`/news?category=${cat}&q=${q}&limit=6&archive_as=destination`} />
    </section>
  );
}

/* Follow/unfollow daily deal-alert emails for this destination (members only). */
function DealAlertButton({ slug, name, country }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get(`/deal-alerts/status?slug=${encodeURIComponent(slug)}`)
      .then((r) => setFollowing(!!r.data.following)).catch(() => {});
  }, [user, slug]);
  if (!user) return (
    <Link to="/login" data-testid="deal-alerts-login"
      className="inline-flex items-center gap-1 text-xs font-700 text-amber-700 hover:underline">
      <Bell className="w-3.5 h-3.5" /> Deal alerts
    </Link>
  );
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post("/deal-alerts/toggle", { slug, name, country });
      setFollowing(!!r.data.following);
      toast.success(r.data.following
        ? `You'll get an email when new ${name} deals appear`
        : `Deal alerts for ${name} turned off`);
    } catch {
      toast.error("Could not update deal alerts — try again");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid="deal-alerts-toggle"
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-700 border transition-colors ${following
        ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
        : "bg-white border-amber-500 text-amber-700 hover:bg-amber-50"}`}>
      {following ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
      {following ? "Deal alerts on" : "Get deal alerts"}
    </button>
  );
}
